import { useReveal } from "@/hooks/useReveal";
import { Activity, Network, BrainCircuit, Presentation } from "lucide-react";

const SERVICES = [
  {
    code: "S.01",
    icon: Activity,
    title: "Supply Chain Diagnostic Review",
    audience:
      "For companies that know something is wrong but cannot isolate where the cost is hiding.",
    focus:
      "Dwell time, empty miles, trailer utilization, vendor scheduling, yard congestion, forecasting misalignment, load planning, cost-to-serve leakage, EDI/API visibility gaps.",
    deliverable:
      "Executive diagnostic report with root-cause map, financial exposure estimate, and prioritized action plan.",
  },
  {
    code: "S.02",
    icon: Network,
    title: "Network Cost & Efficiency Assessment",
    audience:
      "For companies managing transportation, distribution, warehouse, private fleet, or 3PL complexity.",
    focus:
      "Lane optimization, incremental miles, backhaul strategy, private fleet utilization, warehouse alignment, FTL/LTL mix, regional flow analysis.",
    deliverable:
      "Axiom-AI identifies where network design, execution behavior, and cost accounting are misaligned.",
  },
  {
    code: "S.03",
    icon: BrainCircuit,
    title: "AI + Operations Intelligence Advisory",
    audience:
      "For leaders exploring AI-assisted planning, dashboards, control towers, forecasting workflows, exception management, and decision support.",
    focus:
      "Operating-model alignment, signal quality, decision boundaries, exception orchestration, and where AI multiplies value versus where it multiplies noise.",
    deliverable:
      "AI only creates value when it understands the operating system it is being placed inside.",
  },
  {
    code: "S.04",
    icon: Presentation,
    title: "Executive Supply Chain Briefings",
    audience:
      "For boards, executives, investors, private equity teams, and leadership groups.",
    focus:
      "Dwell as congestion debt, empty miles as failed load pairing, cost-to-serve distortion, AI in logistics, EDI/API visibility, private fleet strategy, macro risk, tariffs, China/Mexico supply-chain risk, resilience vs. lean efficiency.",
    deliverable:
      "90-Minute Executive Briefing · Half-Day Strategic Workshop · 30-Day Diagnostic Engagement.",
  },
];

export default function ServicesSection() {
  const { ref, visible } = useReveal();

  return (
    <section
      id="services"
      ref={ref}
      data-testid="services-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="num-label">SECTION 05 / SERVICES</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <h2
              data-testid="services-headline"
              className={`font-display font-bold text-axiom-text tracking-tight text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] reveal ${
                visible ? "in" : ""
              }`}
            >
              Operator-Grade Advisory <br />
              <span className="text-axiom-muted">for Complex Supply Chains.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className={`text-axiom-muted text-base sm:text-lg leading-relaxed reveal ${
                visible ? "in" : ""
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              Axiom-AI supports leadership teams that need to understand where
              cost is hiding, why execution is breaking down, and what
              decisions must change before operational friction becomes
              financial damage.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {SERVICES.map((s, i) => (
            <article
              key={s.code}
              data-testid={`service-card-${s.code}`}
              className={`bg-axiom-bg p-6 md:p-8 group hover:bg-axiom-surface transition-colors duration-300 reveal ${
                visible ? "in" : ""
              }`}
              style={{ transitionDelay: `${100 + i * 90}ms` }}
            >
              <div className="flex items-start justify-between mb-5">
                <div className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-axiom-accent/60 transition-colors">
                  <s.icon size={20} className="text-axiom-accent" strokeWidth={1.4} />
                </div>
                <span className="num-label">{s.code}</span>
              </div>

              <h3 className="font-display text-2xl md:text-3xl text-axiom-text font-bold leading-tight">
                {s.title}
              </h3>

              <p className="mt-4 text-sm text-axiom-muted italic leading-relaxed">
                {s.audience}
              </p>

              <div className="mt-6 border-t border-white/10 pt-5">
                <div className="num-label mb-2">FOCUS AREAS</div>
                <p className="text-sm text-axiom-text/90 leading-relaxed">
                  {s.focus}
                </p>
              </div>

              <div className="mt-5 border-t border-white/10 pt-5">
                <div className="num-label mb-2">DELIVERABLE</div>
                <p className="text-sm text-axiom-accent/90 leading-relaxed">
                  {s.deliverable}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
